import React from "react";
import { ArrowDown, ArrowUp, ClipboardList, Copy, GripVertical, LogIn, MoreHorizontal, Smartphone, Trash2 } from "lucide-react";

import { Chip, IconButton } from "@/shared/components/ui-kit";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { STEPS, stepTitle } from "../blocks";

import type { DraftStep } from "../steps";

export const STEP_ICONS = { questions: ClipboardList, login: LogIn, phone: Smartphone };
const menuItem = "text-[13px] gap-2";

/* One step on the canvas: its number, what it is, and its questions as chips ("label ·
   optional") — or, for a sign-in or phone step, what it asks in a line. A press opens it
   in the inspector. Dragging starts only from the grip, so the card's text can still be
   selected; Move up / Move down in its menu do the same from a keyboard or a phone. */
export function StepCard({ step, index, count, selected, full, onSelect, onMove, onDuplicate, onRemove, drag }: {
  step: DraftStep;
  index: number;
  count: number;
  selected: boolean;
  /** The flow holds all it may: nothing can be duplicated. */
  full: boolean;
  onSelect: () => void;
  onMove: (to: number) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  drag: { armed: boolean; dragging: boolean; onArm: () => void; onStart: () => void; onOver: () => void; onEnd: () => void };
}) {
  const title = stepTitle(step);
  const last = index === count - 1;
  const Icon = STEP_ICONS[step.type];

  return (
    <div
      draggable={drag.armed}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        drag.onStart();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        drag.onOver();
      }}
      onDragEnd={drag.onEnd}
      className={`flex items-start gap-1 p-1.5 rounded-[12px] bg-[var(--surface)] shadow-lift ${
        selected ? "ring-[1.5px] ring-[var(--text-primary)]" : "ring-1 ring-[var(--hairline)]"
      } ${drag.dragging ? "opacity-50" : ""}`}
    >
      <button
        type="button"
        aria-pressed={selected}
        aria-label={`Step ${index + 1}: ${title}`}
        onClick={onSelect}
        className="press flex-1 min-w-0 flex items-start gap-2.5 px-1.5 py-1 rounded-[8px] text-left hover:bg-[var(--accent-hover)]"
      >
        <span className="w-[22px] h-[22px] shrink-0 rounded-full ring-1 ring-[var(--hairline-strong)] flex items-center justify-center font-mono tabular-nums text-[11px] text-[var(--text-secondary)]">
          {index + 1}
        </span>
        <span className="min-w-0 flex-1 flex flex-col gap-1.5 pt-0.5">
          <span className="flex items-center gap-1.5 min-w-0">
            <Icon className="w-[14px] h-[14px] shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
            <span className="min-w-0 truncate text-[13px] font-medium tracking-[-0.005em] text-[var(--text-primary)]">{title}</span>
          </span>
          {step.type === "questions" && step.fields.length > 0 ? (
            <span className="flex flex-wrap gap-1">
              {step.fields.map((q) => (
                <Chip key={q.uid} className={`max-w-full font-normal ${q.label.trim() ? "" : "text-[var(--text-quaternary)]"}`}>
                  <span className="truncate">{(q.label.trim() || "Untitled") + (q.required ? "" : " · optional")}</span>
                </Chip>
              ))}
            </span>
          ) : (
            <span className="text-[11.5px] leading-[1.45] text-[var(--text-tertiary)]">
              {step.type === "questions" ? "No questions yet" : STEPS[step.type].short}
            </span>
          )}
        </span>
      </button>
      <IconButton type="button" aria-label={`Drag ${title} to reorder`} onPointerDown={drag.onArm} className="cursor-grab">
        <GripVertical className="w-3.5 h-3.5" strokeWidth={1.75} />
      </IconButton>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton type="button" aria-label={`${title} options`} className="data-[state=open]:bg-[var(--accent-hover)]">
            <MoreHorizontal className="w-[14px] h-[14px]" strokeWidth={1.75} />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[184px]">
          <DropdownMenuItem className={menuItem} disabled={index === 0} onSelect={() => onMove(index - 1)}>
            <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.75} /> Move up
          </DropdownMenuItem>
          <DropdownMenuItem className={menuItem} disabled={last} onSelect={() => onMove(index + 1)}>
            <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.75} /> Move down
          </DropdownMenuItem>
          {step.type === "questions" && (
            <DropdownMenuItem className={menuItem} disabled={full} onSelect={onDuplicate}>
              <Copy className="w-3.5 h-3.5" strokeWidth={1.75} /> Duplicate
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className={`${menuItem} text-[var(--danger)] focus:text-[var(--danger)]`} onSelect={onRemove}>
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} /> Remove step
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
